// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import client from '@Client';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { handleNotificationError } from '@Helpers/notification';
import { CommentsList } from '@Organisms/Comments/CommentsList/CommentsList';
import { useCurrentUser } from '@Queries/userQueries';
import { CaseOutput, CommentOutput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/locale';
import { computeEventList } from '@Organisms/Comments/eventList';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { CommentForm } from '@Molecules/Comment';

import './DescriptionSider.scss';

interface CaseDescriptionSiderProps {
    data?: CaseOutput;
    readOnly?: boolean;
    dataComment: CommentOutput[];
    itemsPerPage?: number;
    setItemsPerPage: React.Dispatch<React.SetStateAction<number>>;
    onListRefetch: () => void;
}

const CaseDescriptionSider = ({
    data,
    dataComment,
    readOnly = false,
    itemsPerPage = 20,
    setItemsPerPage,
    onListRefetch,
}: CaseDescriptionSiderProps): ReactElement => {
    const local = useLocalMessage();
    const { data: currentUser } = useCurrentUser();

    const scrollElement = useRef<HTMLDivElement>(null);
    const caseId = data?._id || '';

    const { permissions = [], login } = currentUser || {};

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CommentController,
                objectId: caseId,
                parentType: 'case',
                onSuccess: () => {
                    onListRefetch();
                },
                onError: handleNotificationError,
            }),
        [caseId, onListRefetch],
    );

    const onLoadCommentHandler = useCallback(
        (page: number) => {
            setItemsPerPage((prevItems) => prevItems + page * prevItems);
        },
        [setItemsPerPage],
    );

    const readOnlyComment = readOnly || !(permissions as string[]).includes('manageComment');

    useEffect(() => {
        const view = scrollElement.current;
        if (view && dataComment.length) {
            setTimeout(() => {
                view.scrollTop = view.scrollHeight;
            }, 100);
        }
    }, [dataComment]);

    return (
        <div className="case-description__sider">
            <div className="case-description__comment-content" ref={scrollElement}>
                <TitleSection className="case-description__comment-title">{local('comment.section')}</TitleSection>
                {dataComment.length ? (
                    <CommentsList
                        data={dataComment || []}
                        onDelete={eventList.onDelete}
                        onEdit={eventList.onUpdate}
                        onLoadPage={onLoadCommentHandler}
                        showLoad={dataComment.length >= itemsPerPage}
                        currentUserLogin={login}
                        readOnly={readOnlyComment}
                        showExtraDataLinks="alert"
                    />
                ) : (
                    <DisplayEmptyData spin={!data?._id} />
                )}
            </div>
            {readOnlyComment ? null : (
                <div className="case-description__comment-form">
                    <CommentForm
                        placeholder={local('comment.placeholder')}
                        footerText={local('comment.footerIndication')}
                        onSubmit={eventList.onCreate}
                    />
                </div>
            )}
        </div>
    );
};

export default CaseDescriptionSider;
